import { Controller } from "@hotwired/stimulus"

// Combines a <input type="date"> and a <input type="text"> (masked as HH:MM)
// into a single hidden field in YYYY-MM-DDTHH:MM format — the same format
// that <input type="datetime-local"> submits.
//
// The time input uses type="text" with a custom masking controller instead of
// type="time" because native time inputs on macOS follow the OS locale for
// 12/24-hour display and cannot be forced to 24-hour via HTML attributes.
//
// Masking behaviour:
//   - Only digits are accepted
//   - Colon is auto-inserted after 2 digits ("14" → "14:")
//   - Hours validated 00–23, minutes 00–59
//   - Max length 5 characters (HH:MM)
//
// On connect (edit mode): splits the hidden field's existing value into the
// two visible inputs. On input: recombines and dispatches a change event so
// downstream controllers (vessel-conflicts, route-duration) react.
//
// openPicker(): calls showPicker() on the date input so clicking anywhere
// in the field (not just the icon) opens the native picker.
export default class extends Controller {
  static targets = ["date", "time", "output"]

  connect() {
    const existing = this.outputTarget.value
    if (existing && existing.includes("T")) {
      const [date, time] = existing.split("T")
      this.dateTarget.value = date
      this.timeTarget.value = time.slice(0, 5)
    }
  }

  combine() {
    const date = this.dateTarget.value
    const time = this.timeTarget.value

    if (date && /^([01]\d|2[0-3]):[0-5]\d$/.test(time)) {
      this.outputTarget.value = `${date}T${time}`
    } else {
      this.outputTarget.value = ""
    }

    this.outputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Masks the time input to accept only digits, auto-insert colon, and
  // enforce HH:MM format. Called on every keystroke via data-action="input".
  maskTime() {
    const input = this.timeTarget
    let raw = input.value.replace(/[^\d]/g, "").slice(0, 4)

    if (raw.length >= 2) {
      const hours = parseInt(raw.slice(0, 2), 10)
      if (hours > 23) raw = "23" + raw.slice(2)
    }

    if (raw.length >= 4) {
      const minutes = parseInt(raw.slice(2, 4), 10)
      if (minutes > 59) raw = raw.slice(0, 2) + "59"
    }

    if (raw.length > 2) {
      input.value = raw.slice(0, 2) + ":" + raw.slice(2)
    } else {
      input.value = raw
    }

    this.combine()
  }

  // Prevents non-digit keys from being entered (except navigation keys).
  // Called via data-action="keydown".
  filterKeys(event) {
    // Allow: backspace, delete, tab, escape, enter, arrows, home, end
    if ([8, 9, 13, 27, 35, 36, 37, 38, 39, 40, 46].includes(event.keyCode)) {
      return
    }

    // Allow Ctrl/Cmd+A, Ctrl/Cmd+C, Ctrl/Cmd+V, Ctrl/Cmd+X
    if ((event.ctrlKey || event.metaKey) && [65, 67, 86, 88].includes(event.keyCode)) {
      return
    }

    // Block non-digit characters
    if (!/^\d$/.test(event.key)) {
      event.preventDefault()
    }
  }

  openPicker() {
    try {
      this.dateTarget.showPicker()
    } catch {
      // showPicker() may throw if unsupported or element not visible.
      // Fail silently — the calendar icon still works as fallback.
    }
  }
}
